/**
 * Unique-value legend class filters for map definitionExpression + AI
 * attributeFilters.
 */

export type LegendClassValue = string | number;

export type LegendClassFilter = {
  /** ArcGIS unique-value field (e.g. fema_classification). */
  field: string;
  /** Selected class values. Empty / all-selected means no filter. */
  values: LegendClassValue[];
  /** Display labels parallel to values (for chips). */
  labels?: string[];
};

export type LayerDefinitionExpressionParts = {
  time?: string | null;
  legend?: string | null;
};

/** Escape a string for ArcGIS SQL single-quoted literals. */
export function escapeArcGisSqlString(value: string): string {
  return value.replace(/'/g, "''");
}

/** Format one value for an ArcGIS WHERE literal. */
export function formatArcGisSqlLiteral(value: LegendClassValue): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return `'${escapeArcGisSqlString(String(value))}'`;
}

/**
 * Build `field IN (...)` / `field = ...` for selected legend classes. Returns
 * null when values are empty (caller treats as “no legend filter”).
 */
export function buildLegendClassDefinitionExpression(
  field: string,
  values: LegendClassValue[]
): string | null {
  if (!field || values.length === 0) return null;
  if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(field)) {
    throw new Error(`Invalid legend filter field name: ${field}`);
  }
  if (values.length === 1) {
    return `${field} = ${formatArcGisSqlLiteral(values[0])}`;
  }
  const list = values.map((v) => formatArcGisSqlLiteral(v)).join(', ');
  return `${field} IN (${list})`;
}

/** AND-compose non-empty definitionExpression parts. */
export function composeDefinitionExpression(
  parts: Array<string | null | undefined>
): string | null {
  const clauses = parts
    .map((p) => (typeof p === 'string' ? p.trim() : ''))
    .filter((p) => p.length > 0);
  if (clauses.length === 0) return null;
  if (clauses.length === 1) return clauses[0];
  return clauses.map((c) => `(${c})`).join(' AND ');
}

export function composeDefinitionExpressionParts(
  parts: LayerDefinitionExpressionParts | null | undefined
): string | null {
  if (!parts) return null;
  return composeDefinitionExpression([parts.time, parts.legend]);
}

/** Convert a legend class filter into AI attributeFilters (one entry per value). */
export function legendClassFilterToAttributeFilters(
  filter: LegendClassFilter | null | undefined
): Array<{ field: string; value: string | number | boolean }> {
  if (!filter?.field || !filter.values?.length) return [];
  return filter.values.map((value) => ({
    field: filter.field,
    value,
  }));
}

/**
 * Build an ArcGIS WHERE from attributeFilters (OR within a field via IN, AND
 * across fields). Used by AI prefetch when the map FeatureLayer has no
 * definitionExpression.
 */
export function buildAttributeFiltersWhereClause(
  filters?: Array<{ field: string; value: string | number | boolean }>
): string {
  if (!filters?.length) return '1=1';
  const byField = new Map<string, Array<string | number | boolean>>();
  for (const filter of filters) {
    if (!filter?.field || !/^[A-Za-z_][A-Za-z0-9_]*$/.test(filter.field)) {
      continue;
    }
    const list = byField.get(filter.field) ?? [];
    list.push(filter.value);
    byField.set(filter.field, list);
  }
  if (byField.size === 0) return '1=1';
  const parts: string[] = [];
  for (const [field, values] of byField) {
    const stringOrNumber = values.filter(
      (v): v is string | number =>
        typeof v === 'string' || typeof v === 'number'
    );
    if (stringOrNumber.length > 0) {
      const clause = buildLegendClassDefinitionExpression(
        field,
        stringOrNumber
      );
      if (clause) parts.push(clause);
      continue;
    }
    if (values.length === 1 && typeof values[0] === 'boolean') {
      parts.push(`${field} = ${values[0] ? 'TRUE' : 'FALSE'}`);
    }
  }
  return parts.length ? parts.join(' AND ') : '1=1';
}

/** Chip / label summary, e.g. "Destroyed" or "Destroyed, Major". */
export function legendClassFilterLabel(
  filter: LegendClassFilter | null | undefined
): string | undefined {
  if (!filter?.values?.length) return undefined;
  if (filter.labels?.length) {
    const labels = filter.labels.filter(
      (l) => typeof l === 'string' && l.trim()
    );
    if (labels.length) return labels.join(', ');
  }
  return filter.values.map(String).join(', ');
}

/**
 * Whether the current selection is a real constraint vs “all classes”. When
 * selectedCount === totalCount (or total unknown and all selected), no filter.
 */
export function isLegendClassFilterActive(
  selectedValues: LegendClassValue[],
  allValues: LegendClassValue[]
): boolean {
  if (selectedValues.length === 0) return false;
  if (allValues.length === 0) return selectedValues.length > 0;
  if (selectedValues.length >= allValues.length) {
    const allSet = new Set(allValues.map(String));
    return !selectedValues.every((v) => allSet.has(String(v)));
  }
  return true;
}
